import { ConditionOperatorEnumType, CountOperator } from '@reach/shared-types/conditions';
import { ChevronDown, ChevronRight, Link2, Trash2 } from 'lucide-react';
import React from 'react';
import type { RelatedSchemaWithFieldTypes } from '../../engage-segments-tab/segment-definitions-api';
import { FieldConditionRow } from './FieldConditionRow';
import type { BuilderFieldCondition, BuilderRelatedResourceCondition } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

const COUNT_OPERATOR_OPTIONS: { value: CountOperator; label: string }[] = [
  { value: CountOperator.EQ, label: 'exactly' },
  { value: CountOperator.GT, label: 'more than' },
  { value: CountOperator.GTE, label: 'at least' },
  { value: CountOperator.LT, label: 'fewer than' },
  { value: CountOperator.LTE, label: 'at most' },
];

function generateId(): string {
  return crypto.randomUUID();
}

interface RelatedResourceBlockProps {
  condition: BuilderRelatedResourceCondition;
  relatedSchemas: RelatedSchemaWithFieldTypes[];
  onUpdate: (condition: BuilderRelatedResourceCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const RelatedResourceBlock: React.FC<RelatedResourceBlockProps> = ({
  condition,
  relatedSchemas,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const schema = relatedSchemas.find(s => s.id === condition.schemaId);
  const fieldOptions = schema?.fields ?? [];
  const isExpanded = condition.isExpanded;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleCountOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...condition,
      countOperator: e.target.value as CountOperator,
    });
  };

  const handleCountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v) && v >= 0) {
      onUpdate({ ...condition, countValue: v });
    }
  };

  const handleNestedConditionUpdate = (index: number) => (updated: BuilderFieldCondition) => {
    const next = [...condition.conditions];
    next[index] = updated;
    onUpdate({ ...condition, conditions: next });
  };

  const handleNestedConditionDelete = (index: number) => () => {
    const next = condition.conditions.filter((_, i) => i !== index);
    onUpdate({ ...condition, conditions: next });
  };

  const handleAddNestedCondition = () => {
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: fieldOptions[0]?.name ?? '',
      fieldType: fieldOptions[0]?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  return (
    <div className={styles.relatedBlock}>
      <div className={styles.relatedHeader}>
        <div className={styles.relatedTitle}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={disabled}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <Link2 size={14} style={{ color: '#6b7280' }} />
          <span>{condition.schemaName}</span>
        </div>
        <button
          type="button"
          className={styles.relatedTitleButton}
          onClick={onDelete}
          disabled={disabled}
          aria-label="Remove related resource condition">
          <Trash2 size={16} />
        </button>
      </div>

      <div className={styles.relatedCountRow}>
        <span>Contact has</span>
        <select
          className={styles.selectInput}
          value={condition.countOperator}
          onChange={handleCountOperatorChange}
          disabled={disabled}
          style={{ width: 'auto' }}
          aria-label="Count operator">
          {COUNT_OPERATOR_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          value={condition.countValue}
          onChange={handleCountValueChange}
          disabled={disabled}
          className={styles.textInput}
          style={{ width: '4rem' }}
          aria-label="Count value"
        />
        <span>matching {condition.schemaName}</span>
      </div>

      {isExpanded && (
        <div className={styles.relatedNested}>
          <div className={styles.relatedNestedLabel}>WHERE (all must match)</div>
          {condition.conditions.map((c, i) => (
            <FieldConditionRow
              key={c.id}
              condition={c}
              fieldOptions={fieldOptions}
              onUpdate={handleNestedConditionUpdate(i)}
              onDelete={handleNestedConditionDelete(i)}
              disabled={disabled}
            />
          ))}
          <div className={styles.addConditionRow}>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddNestedCondition}
              disabled={disabled || fieldOptions.length === 0}>
              + Add condition
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
